import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { businessInfo, mockContactSubmit, TRAVEL_FEE_INFO } from '../utils/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await mockContactSubmit(formData);
      if (result.success) {
        toast.success(result.message);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-4 py-2 mb-4">
            <span className="text-red-500 font-semibold text-sm">Get in Touch</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Contact Us
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out anytime.
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">Let's Talk</h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                Ready to give your vehicle the care it deserves? Contact us today for a quote or to schedule your detailing service.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Phone</h4>
                      <a
                        href={`tel:${businessInfo.phone}`}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Email</h4>
                      <a
                        href={`mailto:${businessInfo.email}`}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-2">Service Area</h4>
                      <p className="text-gray-300 mb-2"><span className="font-semibold">Based in:</span> {businessInfo.address}</p>
                      <p className="text-sm text-gray-400 mb-3">{businessInfo.serviceArea}</p>
                      <div className="space-y-2 text-sm">
                        <div>
                          <p className="text-green-400 font-medium">✓ FREE Service Areas:</p>
                          <p className="text-gray-400">{TRAVEL_FEE_INFO.freeZones.join(', ')}</p>
                        </div>
                        <div>
                          <p className="text-blue-400 font-medium">Extended Coverage:</p>
                          <p className="text-gray-400">{TRAVEL_FEE_INFO.extendedZones.map(z => z.name).join(', ')}</p>
                          <p className="text-xs text-gray-500 mt-1">Travel surcharge: ${TRAVEL_FEE_INFO.extendedZones[0].fee}-${TRAVEL_FEE_INFO.extendedZones[TRAVEL_FEE_INFO.extendedZones.length-1].fee}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Google Review Card */}
              <Card className="bg-gradient-to-br from-red-600/10 to-red-700/5 border-red-500/30 hover:border-red-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl">⭐</span>
                    </div>
                    <h4 className="text-white font-semibold mb-2">Love Our Service?</h4>
                    <p className="text-gray-400 text-sm mb-4">Share your experience with others</p>
                    <a
                      href="https://g.page/r/CS8IeFV59cyJEBM/review"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-all hover:scale-105 text-sm"
                    >
                      Leave a Review
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Send a Message</CardTitle>
              <CardDescription className="text-gray-400">
                Fill out the form and we'll get back to you as soon as possible
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-white">
                    Name *
                  </Label>
                  <Input
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your name"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-white">
                    Email *
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-phone" className="text-white">
                    Phone
                  </Label>
                  <Input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Your phone number"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-white">
                    Message *
                  </Label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us how we can help..."
                    rows={5}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6 font-semibold group"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && (
                    <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
